'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FacilityData } from '@/app/_lib/parseFacilities';
import LoadingSpinner from '@/app/_lib/ui/LoadingSpinner';

// Dynamically import the Map component with no SSR
const DynamicMapComponent = dynamic(() => import('./Map'), {
  ssr: false,
  loading: () => <LoadingSpinner message='Loading map...' />,
});

interface EnhancedMapWrapperProps {
  facilities: FacilityData[];
  selectedFacility: FacilityData | null;
}

export function EnhancedMapWrapper({ facilities, selectedFacility }: EnhancedMapWrapperProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [shouldLoadMap, setShouldLoadMap] = useState(false);
  const infoSectionRef = useRef<HTMLDivElement>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapKey, setMapKey] = useState(0);

  useEffect(() => {
    if (!mapRef.current) return;

    // Add a small delay to ensure the element is properly rendered
    const timer = setTimeout(() => {
      if (!mapRef.current) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // If map container is visible in viewport
            if (entry.isIntersecting) {
              // Set state to load the map
              setShouldLoadMap(true);
              // Disconnect observer after map is loaded
              observer.disconnect();
            }
          });
        },
        {
          // Start loading when map is 200px from entering viewport
          rootMargin: '200px 0px',
          threshold: 0,
        },
      );

      // Start observing the map container
      observer.observe(mapRef.current);

      // Clean up function
      return () => {
        observer.disconnect();
      };
    }, 100); // Small delay to ensure DOM is ready

    // Clean up
    return () => {
      clearTimeout(timer);
    };
  }, []);

  // Effect to add 'is-visible' class when map is loaded and infoSectionRef is available
  useEffect(() => {
    if (mapLoaded && infoSectionRef.current) {
      infoSectionRef.current.classList.add('is-visible');
    }
  }, [mapLoaded]);

  // Force map re-render when selected facility changes to update highlighting
  useEffect(() => {
    if (selectedFacility && mapLoaded) {
      setMapKey((prev) => prev + 1);
    }
  }, [selectedFacility, mapLoaded]);

  const handleInfoSectionRef = useCallback((ref: HTMLDivElement | null) => {
    infoSectionRef.current = ref;
  }, []);

  const handleMapLoad = useCallback(() => {
    setMapLoaded(true);
  }, []);

  return (
    <div
      ref={mapRef}
      id='map-section'
      className='relative h-[440px] overflow-hidden rounded-xl bg-gray-50 shadow-lg md:h-[540px] dark:bg-gray-900'
    >
      {shouldLoadMap ? (
        <DynamicMapComponent
          key={mapKey}
          onInfoSectionRef={handleInfoSectionRef}
          onLoad={handleMapLoad}
          facilities={facilities}
          selectedFacility={selectedFacility}
        />
      ) : (
        <div className='flex h-full items-center justify-center bg-gray-100 dark:bg-gray-800'>
          <LoadingSpinner message='Map will load when visible' />
          <div className='absolute bottom-4 left-4 text-xs text-gray-500'>Scroll to this section to load the map</div>
        </div>
      )}
    </div>
  );
}
